import type { TrainingAssignmentResponse } from "@lms/shared-types";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiRequestError, fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, ErrorState, LoadingState, PageHeader, StatusPill, TextField } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { useFormat } from "../../lib/format";
import { durationWords, hoursWord, rtiWords, SOURCE_WORD, STATUS_WORD } from "../../lib/training";

/** Every mutation shows its error, in the API's own words when it has them (qa-battery.md §1.1 rule 4). */
function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) return error.requestId ? `${error.message} (request ${error.requestId})` : error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

/**
 * One assigned course (CNT-20) and the two things that finish it (CNT-25):
 * the learner says they did it, and somebody with standing agrees.
 *
 * The course itself is taken on the provider's site — Cohort 1's training
 * is vendor-delivered (ADR-0019), so the honest screen is a link out and a
 * way back in, not a player pretending to run somebody else's course.
 */
export function TrainingItemPage() {
  const { id = "" } = useParams();
  const { session, config, refreshMe } = useAuth();
  const { date } = useFormat();
  const announce = useAnnounce();
  const [state, setState] = useState<{ data?: TrainingAssignmentResponse; loading: boolean; error?: unknown }>({ loading: true });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>();
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<TrainingAssignmentResponse>(config, session, `/v1/training/assignments/${encodeURIComponent(id)}`)
      .then((data) => live && setState({ data, loading: false }))
      .catch((error: unknown) => live && setState({ loading: false, error }));
    return () => {
      live = false;
    };
  }, [config, session, id, tick]);

  const act = async (path: string, body: unknown, said: string) => {
    if (!session) return;
    setBusy(true);
    setActionError(undefined);
    try {
      const data = await fetchJson<TrainingAssignmentResponse>(config, session, `/v1/training/assignments/${encodeURIComponent(id)}/${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      setState({ data, loading: false });
      setNote("");
      announce(said);
      // /v1/me's facts count late courses (PLT-06): the nudge must follow the claim without a reload. A failed refresh leaves the old count, not an error.
      void refreshMe().catch(() => undefined);
    } catch (error: unknown) {
      setActionError(error);
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <>
        <PageHeader title="Training" />
        <LoadingState label="Loading this course…" />
      </>
    );
  }
  if (!state.data) {
    return (
      <>
        <PageHeader title="Training" />
        <ErrorState title="This course could not be loaded." error={state.error} onRetry={reload} />
        <p className="mt-4">
          <Link to="/training">Back to My training</Link>
        </p>
      </>
    );
  }

  const { assignment: a, mayAttest } = state.data;
  const status = STATUS_WORD[a.status];
  const duration = durationWords(a.item.durationMinutes);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to="/training">My training</Link>
            {/* The state, named, where focus lands after a route change — and the one place a test reads it from. */}
            <span data-testid="training-status" className="flex flex-wrap items-center gap-2">
              {a.overdue && <StatusPill tone="danger">Late</StatusPill>}
              <StatusPill tone={status.tone}>{status.word}</StatusPill>
            </span>
          </>
        }
        title={a.item.title}
        subtitle={a.why}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <div className="space-y-6">
          <Card title="What this is">
            <p className="max-w-prose">{a.item.summary}</p>
            {a.item.accessibilityNote && (
              <>
                <h3 className="mt-4 text-sm font-semibold text-ink">Getting at it</h3>
                <p className="mt-1 max-w-prose text-sm text-muted">{a.item.accessibilityNote}</p>
              </>
            )}
            {a.item.externalUrl && (
              <p className="mt-4">
                {/* Leaving the product is said out loud, and the provider is named: nobody should be surprised by a new tab. */}
                <a href={a.item.externalUrl} target="_blank" rel="noreferrer noopener">
                  Open the course on {a.item.provider ?? "the provider's site"} (opens in a new tab)
                </a>
              </p>
            )}
          </Card>

          {a.status === "assigned" && (
            <Card title="When you have finished it">
              <p className="max-w-prose text-muted">Tell us you are done and your program director checks it. Nothing counts towards your apprenticeship until they have.</p>
              <form
                className="mt-4 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void act("completion", { source: note.trim() ? "certificate" : "self_report", ...(note.trim() ? { evidenceNote: note.trim() } : {}) }, `${a.item.title} marked as done, waiting to be checked`);
                }}
              >
                <TextField label="Certificate number or a note (optional)" description="Anything that helps whoever checks it — a certificate number, the date you sat it. Do not put anyone else's details here." value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className="max-w-md" />
                <Button type="submit" variant="primary" disabled={busy}>
                  {busy ? "Working…" : "I have finished this"}
                </Button>
              </form>
            </Card>
          )}

          {mayAttest && a.completion && (
            <Card title="Check this completion">
              <p className="max-w-prose text-muted">
                {a.completion.claimedBy.displayName} says this is done{a.completion.evidenceNote ? <>, and left a note: “{a.completion.evidenceNote}”</> : null}. Confirming it records the completion against their program
                {a.rti.state === "pending" && a.rti.planHours ? <> and banks {hoursWord(a.rti.planHours)} of RTI in their hours ledger</> : null}.
              </p>
              <form
                className="mt-4 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void act("attestation", note.trim() ? { note: note.trim() } : {}, `${a.item.title} confirmed as done`);
                }}
              >
                <TextField label="What you checked (optional)" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className="max-w-md" />
                <Button type="submit" variant="primary" disabled={busy}>
                  {busy ? "Working…" : "Confirm it is done"}
                </Button>
              </form>
            </Card>
          )}

          {actionError !== undefined && <ErrorState title="That did not work." error={errorText(actionError)} onRetry={reload} retryLabel="Reload this course" />}
        </div>

        <Card title="Details">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {a.item.provider && (
              <>
                <dt className="text-muted">Provider</dt>
                <dd>{a.item.provider}</dd>
              </>
            )}
            {duration && (
              <>
                <dt className="text-muted">How long</dt>
                <dd>about {duration}</dd>
              </>
            )}
            {/* CNT-26: the plan's number and the ledger's answer, never the item's suggestion — and why, when it is none. */}
            <dt className="text-muted">Apprenticeship hours</dt>
            <dd data-testid="training-rti">{rtiWords(a.rti, date)}</dd>
            <dt className="text-muted">Assigned</dt>
            <dd>
              <time dateTime={a.assignedAt}>{date(a.assignedAt)}</time>
            </dd>
            {a.dueAt && (
              <>
                <dt className="text-muted">Wanted by</dt>
                <dd>
                  <time dateTime={a.dueAt}>{date(a.dueAt)}</time>
                </dd>
              </>
            )}
            {a.completion && (
              <>
                <dt className="text-muted">You said</dt>
                <dd>
                  {SOURCE_WORD[a.completion.source]} on <time dateTime={a.completion.claimedAt}>{date(a.completion.claimedAt)}</time>
                </dd>
              </>
            )}
            {a.completion?.attestedAt && (
              <>
                <dt className="text-muted">Checked by</dt>
                <dd>
                  {a.completion.attestedBy?.displayName}
                  {a.completion.selfAttested ? " (you confirmed your own)" : ""} on <time dateTime={a.completion.attestedAt}>{date(a.completion.attestedAt)}</time>
                </dd>
              </>
            )}
          </dl>
        </Card>
      </div>
    </>
  );
}
